import { Permission } from '@bookora/shared';

import type { TourDef } from './types';

/**
 * «ثبت اطلاعات از روی عکس», before reading: the landing (`/import/photo`,
 * the kind picker) and the capture screen (`/import/photo?kind=…`). A draft
 * job's own page (`/import/photo/:jobId`) is `ai-import-review`'s route, so
 * this tour does not run there. The consent gate hides the capture anchors
 * until the clinic has agreed, so the steps skip themselves before that.
 *
 * The kind picker and the capture screen are never on the page together
 * (`landing-page.tsx` renders one or the other), so a run shows either the
 * kind step alone (a manual start on the landing; never auto-offered, one
 * step is under `minSteps`) or capture + start. The kind step's body says
 * where picking leads, so the landing run is not a dead end.
 *
 * The capture screen swaps FileDrop + «ادامه روی گوشی» for camera buttons
 * on a coarse pointer (a tablet can be `wide`), so the capture body names
 * «ادامه روی گوشی» only as a desktop option. The page strip's order/rotate
 * hint is folded into it (critic C20: one tour, three steps). The review
 * after reading is `ai-import-review`.
 */
export const AI_IMPORT_CAPTURE_TOUR: TourDef = {
  key: 'ai-import-capture',
  version: 1,
  surface: 'page',
  title: 'عکس گرفتن از برگه‌ها',
  hubDescription: 'انتخاب نوع برگه، عکس صفحه‌ها و شروع خواندن.',
  offer: {
    body: 'در چند مرحله نشونتون می‌دیم چطور از برگه‌ها عکس بگیرید تا اطلاعاتشون درست خوانده بشه.',
  },
  route: { path: '/import/photo', match: (pathname) => pathname === '/import/photo' },
  requires: { all: [Permission.IMPORT_AI] },
  requiresFeature: 'aiImport',
  autoOffer: true,
  maxSteps: 3,
  minSteps: 2,
  steps: [
    {
      id: 'kind',
      anchor: 'import.kind-picker',
      placement: 'bottom',
      title: 'از روی چه برگه‌ای؟',
      body: 'نوع برگه رو انتخاب کنید؛ مثلاً فهرست بیماران، دفتر نوبت‌ها یا فاکتور خرید مواد. هر نوع در بخش خودش ثبت می‌شه. با انتخاب نوع، صفحهٔ عکس گرفتن باز می‌شه.',
    },
    {
      id: 'capture',
      anchor: 'import.capture',
      placement: 'bottom',
      title: 'عکس صفحه‌ها',
      body: (ctx) =>
        ctx.viewport === 'compact'
          ? 'از هر صفحه یک عکس صاف و روشن بگیرید و صفحه‌ها رو پشت‌سرهم اضافه کنید. ترتیب عکس‌ها ترتیب خواندن است و می‌تونید عکس‌های کج رو بچرخونید.'
          : 'عکس هر صفحه رو اینجا اضافه کنید، یا روی کامپیوتر با «ادامه روی گوشی» از دوربین گوشی کمک بگیرید. ترتیب عکس‌ها ترتیب خواندن است و می‌تونید عکس‌های کج رو بچرخونید.',
    },
    {
      id: 'start',
      anchor: 'import.start',
      placement: 'top',
      title: 'شروع خواندن',
      body: 'خواندن عکس‌ها چند دقیقه طول می‌کشه. لازم نیست در این صفحه منتظر بمونید؛ وقتی آماده شد، در اعلان‌ها خبرتون می‌کنیم. تا بازبینی نکنید، چیزی ثبت نمی‌شه.',
    },
  ],
};
